import { ChevronDown } from "lucide-react";
import { type SelectHTMLAttributes, forwardRef, useId } from "react";
import { type FieldSize, fieldClasses, fieldErrorClass, fieldLabelClass } from "./field.ts";

export interface SelectOption {
	value: string;
	label: string;
	disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
	label?: string;
	error?: string;
	options: SelectOption[];
	placeholder?: string;
	size?: FieldSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
	(
		{ label, error, options, placeholder, size = "md", className = "", id: idProp, ...props },
		ref,
	) => {
		const generatedId = useId();
		const id = idProp ?? generatedId;
		const errorId = `${id}-error`;

		return (
			<div className="flex flex-col gap-1.5">
				{label && (
					<label htmlFor={id} className={fieldLabelClass}>
						{label}
					</label>
				)}
				<div className="relative">
					<select
						ref={ref}
						id={id}
						aria-invalid={error ? "true" : undefined}
						aria-describedby={error ? errorId : undefined}
						className={fieldClasses(size, !!error, `appearance-none pr-9 ${className}`)}
						{...props}
					>
						{placeholder && (
							<option value="" disabled>
								{placeholder}
							</option>
						)}
						{options.map((option) => (
							<option key={option.value} value={option.value} disabled={option.disabled}>
								{option.label}
							</option>
						))}
					</select>
					<ChevronDown
						size={16}
						className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted"
					/>
				</div>
				{error && (
					<p id={errorId} role="alert" className={fieldErrorClass}>
						{error}
					</p>
				)}
			</div>
		);
	},
);

Select.displayName = "Select";
